import React, { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Filter } from "iconsax-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/view/components/ui/popover";
import { Button } from "@/view/components/ui/button";
import {
  differenceInDays,
  endOfDay,
  endOfMonth,
  formatDate,
  isValid,
  parse,
  parseISO,
  startOfDay,
  startOfMonth,
  format,
} from "date-fns";
import Dates from "./Dates";
import Values from "./Values";
import TransactionStatus from "./TransactionStatus";
import Teams from "./Teams";
import Sellers from "./Sellers";
import Leaders from "./Leaders";
import Managers from "./Managers";
import CommissionType from "./CommissionType";
import SortByUser from "./SortByUser";
import { MultiValue } from "react-select";
import { OptionType } from "@/app/utils/optionSelectType";
import Supervisors from "./Supervisors";
import SupportType from "./SupportType";
import SupportUsers from "./SupportUsers";
import SortByCustomerStatus from "./SortByCustomerStatus";
import TransactionDates from "./TransactionDates";

type FilterParams = {
  startDate?: string | undefined;
  endDate?: string | undefined;
  minValue?: string | undefined;
  maxValue?: string | undefined;
};

const parseDateInputValue = (value?: string | Date) => {
  if (!value) return undefined;

  if (value instanceof Date) {
    return isValid(value) ? value : undefined;
  }

  const sanitized = value.trim();
  if (!sanitized) return undefined;

  const parsedFromMask = parse(sanitized, "dd/MM/yyyy", new Date());
  if (isValid(parsedFromMask)) return parsedFromMask;

  const parsedFromIso = parseISO(sanitized);
  return isValid(parsedFromIso) ? parsedFromIso : undefined;
};

const safeDateTransform = (
  value: string | undefined,
  modifier: (date: Date) => Date,
) => {
  const parsed = parseDateInputValue(value);
  if (!parsed) return undefined;
  const modified = modifier(parsed);
  // Formata preservando o timezone local
  return format(modified, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx");
};

const formSchema = z.object({
  startDate: z
    .string()
    .optional()
    .transform((f) => safeDateTransform(f, startOfDay)),
  endDate: z
    .string()
    .optional()
    .transform((f) => safeDateTransform(f, endOfDay)),
  transactionStartDate: z
    .string()
    .optional()
    .transform((f) => safeDateTransform(f, startOfDay)),
  transactionEndDate: z
    .string()
    .optional()
    .transform((f) => safeDateTransform(f, endOfDay)),
  minValue: z
    .string()
    .optional()
    .transform((f) => (f ? f.replace(/\D/g, "") : undefined)),
  maxValue: z
    .string()
    .optional()
    .transform((f) => (f ? f.replace(/\D/g, "") : undefined)),
  status: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  type: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  teamIds: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  sellerIds: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  leaderIds: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  supervisorIds: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  managerIds: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  supportIds: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  supportType: z
    .string()
    .array()
    .optional()
    .transform((f) => {
      if (!f || !f.length || f.length < 1) return undefined;
      let res = "";
      for (const status of f) res += status + ",";
      return res.slice(0, -1);
    }),
  sort: z.string().optional(),
  customerStatus: z.string().optional(),
});

export type FilterFormData = z.infer<typeof formSchema>;

const fieldFilterMap: FilterFormData = {
  startDate: "De ",
  endDate: "Até ",
  minValue: "Valor mínimo ",
  maxValue: "Valor máximo ",
  status: "Status ",
  type: "Tipo ",
  leaderIds: "Lideres ",
  managerIds: "Gerentes ",
  supervisorIds: "Supervisores ",
  sellerIds: "Vendedores ",
  sort: "",
  teamIds: "Equipes ",
  supportIds: "Suportes",
  supportType: "Tipo de suporte",
  customerStatus: "",
};

export type FiltersType =
  | "Date"
  | "TransactionDates"
  | "Value"
  | "TransactionStatus"
  | "Teams"
  | "Sellers"
  | "Leaders"
  | "Supervisors"
  | "Managers"
  | "Type"
  | "SortByUser"
  | "Supports"
  | "SupportType"
  | "SortByCustomerStatus";

type FilterReportsProps = {
  setParams: React.Dispatch<React.SetStateAction<FilterParams | undefined>>;
  filters: FiltersType[];
  isCouponOrReferrals?: boolean;
};
export const FilterReports = ({
  setParams,
  filters,
  isCouponOrReferrals = false,
}: FilterReportsProps) => {
  const [selectedSellerOptions, setSelectedSellerOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedLeaderOptions, setSelectedLeaderOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedSupervisorsOptions, setSelectedSupervisorsOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedManagerOptions, setSelectedManagerOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedSupervisorOptions, setSelectedSupervisorOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedTeamOptions, setSelectedTeamOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [selectedSupportOptions, setSelectedSupportOptions] = useState<
    MultiValue<OptionType>
  >([]);

  const filtersDic = {
    Date: <Dates />,
    TransactionDates: <TransactionDates />,
    Value: <Values />,
    TransactionStatus: <TransactionStatus />,
    Teams: <Teams opt={selectedTeamOptions} setOpt={setSelectedTeamOptions} />,
    Sellers: (
      <Sellers
        isCouponOrReferrals={isCouponOrReferrals}
        opt={selectedSellerOptions}
        setOpt={setSelectedSellerOptions}
      />
    ),
    Leaders: (
      <Leaders opt={selectedLeaderOptions} setOpt={setSelectedLeaderOptions} />
    ),
    Supervisors: (
      <Supervisors
        opt={selectedSupervisorsOptions}
        setOpt={setSelectedSupervisorsOptions}
      />
    ),
    Managers: (
      <Managers
        opt={selectedManagerOptions}
        setOpt={setSelectedManagerOptions}
      />
    ),
    Supervisor: (
      <Supervisors
        opt={selectedSupervisorOptions}
        setOpt={setSelectedSupervisorOptions}
      />
    ),
    Type: <CommissionType />,
    SortByUser: <SortByUser />,
    Supports: (
      <SupportUsers
        opt={selectedSupportOptions}
        setOpt={setSelectedSupportOptions}
      />
    ),
    SupportType: <SupportType />,
    SortByCustomerStatus: <SortByCustomerStatus />,
  };

  const formatDateInput = (date: Date) => {
    date.setHours(0, 0, 0, 0);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
  };

  const [isOpen, setIsOpen] = useState(false);
  const filterForm = useForm<FilterFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: filters.includes("TransactionDates")
      ? {
          transactionStartDate: formatDateInput(startOfMonth(new Date())),
          transactionEndDate: formatDateInput(endOfMonth(new Date())),
        }
      : {
          startDate: formatDateInput(startOfMonth(new Date())),
          endDate: formatDateInput(endOfMonth(new Date())),
        },
  });

  const { handleSubmit, reset, watch, setValue } = filterForm;

  const fieldValues = watch();

  const filterTexts = useCallback(() => {
    const filterText = Object.entries(fieldValues)
      .filter(([key, value]) => value && !key.includes("Date"))
      .map(([key, value]) => {
        if (key === "sort") {
          return value === "user" ? "Agrupado por vendedor" : null;
        }

        if (Array.isArray(value)) {
          return `${value.length} ${fieldFilterMap[key as keyof typeof fieldFilterMap]}`;
        }

        return `${fieldFilterMap[key as keyof typeof fieldFilterMap] as string}: ${value}`;
      })
      .filter(Boolean);

    const dateString = calculateDaysDifference(
      fieldValues.startDate,
      fieldValues.endDate,
    );

    if (fieldValues.customerStatus === "HAS_AMOUNT") {
      return [dateString, "Com saldo"].join(" | ");
    }

    if (fieldValues.customerStatus === "ARCHIVED") {
      return [dateString, "Arquivado"].join(" | ");
    }

    if (fieldValues.customerStatus === "UNAUTHORIZED") {
      return [dateString, "Não autorizado"].join(" | ");
    }

    if (
      fieldValues.startDate === formatDateInput(startOfMonth(new Date())) &&
      fieldValues.endDate === formatDateInput(endOfMonth(new Date()))
    ) {
      return ["Este mês", ...filterText].join(" | ");
    }

    if (dateString) return [dateString, ...filterText].join(" | ");
    return filterText.join(" | ");
  }, [fieldValues]);

  const calculateDaysDifference = (
    startDate: string | undefined,
    endDate: string | undefined,
  ) => {
    if (!endDate || !startDate) {
      if (startDate) return `De ${formatDate(startDate, "dd/MM/yyyy")}`;
      if (endDate) return `até ${formatDate(endDate, "dd/MM/yyyy")}`;
      return "";
    }

    const start = parseISO(startDate);
    const end = parseISO(endDate);
    return `${differenceInDays(end, start) + 1} dias`;
  };

  const onSubmit = useCallback(
    (data: FilterFormData) => {
      setParams(data);
      setIsOpen(false);
    },
    [setParams],
  );

  const handleClearFilter = () => {
    reset();
    setParams({
      startDate: startOfMonth(new Date()).toISOString(),
      endDate: endOfMonth(new Date()).toISOString(),
    });
    setValue("customerStatus", "");
  };

  return (
    <div className="flex items-center justify-between">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="flex cursor-pointer items-center gap-2 rounded-lg border-[1px] border-gray01 px-3 py-2">
            <Filter size="20" />
            {filterTexts() ? (
              <p className="text-xs font-semibold">{filterTexts()}</p>
            ) : (
              <p className="text-xs font-semibold">Filtrar</p>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent className="ml-[264px] w-[620px] max-w-[90vw] p-0">
          <FormProvider {...filterForm}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="m-4 flex flex-col gap-4 max-md:w-full md:gap-5">
                <div className="flex flex-wrap items-end gap-4">
                  {filters.includes("Date") && (
                    <div className="min-w-[calc(50%-0.5rem)] flex-1 max-md:min-w-full">
                      {filtersDic.Date}
                    </div>
                  )}
                  {filters.includes("TransactionDates") && (
                    <div className="min-w-[calc(50%-0.5rem)] flex-1 max-md:min-w-full">
                      {filtersDic.TransactionDates}
                    </div>
                  )}
                  {filters.includes("SortByUser") && (
                    <div className="min-w-[calc(50%-0.5rem)] flex-1 max-md:min-w-full">
                      {filtersDic.SortByUser}
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-4">
                  {filters
                    .filter(
                      (f) =>
                        f !== "Date" &&
                        f !== "TransactionDates" &&
                        f !== "SortByUser",
                    )
                    .map((f) => (
                      <div
                        key={f}
                        className="min-w-[calc(50%-0.5rem)] flex-1 max-md:min-w-full"
                      >
                        {filtersDic[f]}
                      </div>
                    ))}
                </div>
              </div>

              <div className="my-5 w-full border-b-[1px] border-gray01" />

              <div className="flex items-center justify-between p-4">
                <Button
                  type="button"
                  onClick={handleClearFilter}
                  variant="outline"
                  className="w-24 rounded-full bg-gray02 text-xs font-bold"
                >
                  Limpar
                </Button>
                <Button
                  type="submit"
                  className="w-20 rounded-full bg-primary text-xs font-bold text-white01"
                >
                  Filtrar
                </Button>
              </div>
            </form>
          </FormProvider>
        </PopoverContent>
      </Popover>
    </div>
  );
};
